import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload, ImagePlus, Loader2, Send, X, ArrowLeft, Lock, CalendarIcon, Route as RouteIcon, Fuel, FileText, Plus, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { safeConferenceReturn } from "@/lib/conference";
import { businessDateStr } from "@/lib/businessDate";

const todayStr = () => businessDateStr();

export default function NewClosing() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user: authUser } = useAuth();
  const behalf = authUser?.role !== "motorista" ? params.get("driver_id") : null;
  const editId = params.get("id");
  const [targetDriver, setTargetDriver] = useState(null);
  const user = behalf ? targetDriver : authUser;
  useEffect(() => {
    if (behalf) api.get("/operations/drivers").then(r => {
      const driver = r.data.find(d => d.id === behalf);
      if (driver) { setTargetDriver(driver); if (!editId) setRotaLabel(driver.rota || ""); }
      else toast.error("Motorista não encontrado ou fora da sua equipe");
    }).catch(e => toast.error(formatApiError(e)));
  }, [behalf, editId]);
  const initialDate = params.get("date") || todayStr();

  const [closingDate, setClosingDate] = useState(initialDate);
  const [rotaLabel, setRotaLabel] = useState(user?.rota || "");
  const [regiao, setRegiao] = useState("");
  const [entregues, setEntregues] = useState("");
  const [retornos, setRetornos] = useState("");
  const [foraQtd, setForaQtd] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [jadlog, setJadlog] = useState(null);
  const [foraFiles, setForaFiles] = useState([]);
  const [fuelUsed, setFuelUsed] = useState(false);
  const [fuelValor, setFuelValor] = useState("");
  const [fuelData, setFuelData] = useState("");
  const [fuelObs, setFuelObs] = useState("");
  const [fuelFile, setFuelFile] = useState(null);
  const [devolucaoQtd, setDevolucaoQtd] = useState("");
  const [devolucaoFiles, setDevolucaoFiles] = useState([]);
  const [onlogQtd, setOnlogQtd] = useState("");
  const [onlogFiles, setOnlogFiles] = useState([]);
  const [remessasArQtd, setRemessasArQtd] = useState("");
  const [coletasQtd, setColetasQtd] = useState("");
  const [modalidade, setModalidade] = useState("normal");
  const [viagens, setViagens] = useState([{ rotaSetor: "", remessasQtd: "", file: null }]);
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(null);
  const [fetching, setFetching] = useState(!!editId);
  const [fetchError, setFetchError] = useState("");
  const jadlogRef = useRef(null);
  const foraRef = useRef(null);
  const fuelRef = useRef(null);
  const devolucaoRef = useRef(null);
  const onlogRef = useRef(null);

  // Quando editId presente, busca o fechamento específico
  useEffect(() => {
    if (!editId) return;
    setFetching(true);
    api.get(`/closings/${editId}`)
      .then((r) => {
        const doc = r.data;
        setExisting(doc);
        setClosingDate(doc.date);
        setRotaLabel(doc.rota || "");
        setRegiao(doc.regiao || "");
        setEntregues(String(doc.entregues ?? ""));
        setRetornos(String(doc.retornos ?? ""));
        setForaQtd(String(doc.fora_lista_qtd ?? ""));
        setObservacoes(doc.observacoes ?? "");
        setJadlog(null);
        setForaFiles([]);
        setFuelUsed(!!doc.abastecimento_used);
        setFuelValor(doc.abastecimento_valor ? String(doc.abastecimento_valor) : "");
        setFuelData(doc.abastecimento_data || "");
        setFuelObs(doc.abastecimento_obs || "");
        setFuelFile(null);
        setDevolucaoQtd(String(doc.devolucao_qtd ?? ""));
        setDevolucaoFiles([]);
        setOnlogQtd(String(doc.onlog_qtd ?? (doc.onlog_comprovante ? 1 : "")));
        setOnlogFiles([]);
        setRemessasArQtd(String(doc.remessas_ar_qtd ?? ""));
        setColetasQtd(String(doc.coletas_qtd ?? ""));
        setModalidade(doc.modalidade_fechamento || "normal");
        setViagens((doc.viagens || []).length ? doc.viagens.map((v) => ({ rotaSetor: v.rota_setor || "", remessasQtd: String(v.remessas_qtd || ""), file: null, existing: v.comprovante })) : [{ rotaSetor: "", remessasQtd: "", file: null }]);
      })
      .catch((err) => { setFetchError(formatApiError(err)); toast.error(formatApiError(err)); })
      .finally(() => setFetching(false));
  }, [editId]);

  const total = (Number(entregues) || 0) + (Number(retornos) || 0);
  const sucesso = total > 0 ? ((Number(entregues) || 0) / total * 100).toFixed(2) : "0.00";
  const foraQtdNum = Number(foraQtd) || 0;
  const isApproved = existing?.status === "conferido";
  const isRejected = existing?.status === "rejeitado";
  const staffEdit = !!behalf && ["admin", "supervisor"].includes(authUser?.role);
  const lockedReadOnly = (isApproved && !staffEdit) || existing?.archived;
  const isEdit = !!editId && !!existing;

  function validImage(file) {
    return file && ["image/jpeg", "image/jpg", "image/png"].includes(file.type);
  }
  function validFuelComp(file) {
    return file && ["image/jpeg", "image/jpg", "image/png", "application/pdf"].includes(file.type);
  }
  function onPickJadlog(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!validImage(f)) { toast.error("Use apenas JPG, JPEG ou PNG"); return; }
    setJadlog(f);
  }
  function onPickFora(e) {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(validImage);
    if (valid.length !== files.length) toast.warning("Alguns arquivos foram ignorados (use JPG/PNG)");
    setForaFiles((prev) => [...prev, ...valid]);
  }
  function removeFora(idx) {
    setForaFiles((prev) => prev.filter((_, i) => i !== idx));
  }
  function onPickDevolucao(e) {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(validImage);
    if (valid.length !== files.length) toast.warning("Alguns arquivos foram ignorados (use JPG/PNG)");
    setDevolucaoFiles(valid);
  }
  function onPickOnlog(e) {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(validImage);
    if (valid.length !== files.length) toast.warning("Alguns arquivos foram ignorados (use JPG/PNG)");
    setOnlogFiles(valid);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (lockedReadOnly || fetchError || fetching || (behalf && !targetDriver)) return;
    if (!regiao) { toast.error("Selecione a lista de entrega realizada (Guarulhos ou São Paulo)"); return; }
    if (modalidade === "normal" && !rotaLabel.trim()) { toast.error("Informe a identificação da rota"); return; }
    if (modalidade === "normal" && (entregues === "" || retornos === "")) { toast.error("Informe Entregues e Retornos"); return; }
    if (modalidade === "normal" && !isEdit && !jadlog) { toast.error("Anexe o print da Jadlog"); return; }
    if (modalidade === "por_viagem") {
      if (!viagens.length) { toast.error("Adicione pelo menos uma viagem"); return; }
      const invalid = viagens.findIndex((v) => !v.rotaSetor.trim() || !(Number(v.remessasQtd) > 0) || (!v.file && !v.existing));
      if (invalid >= 0) { toast.error(`Complete a viagem ${invalid + 1}: rota/setor, remessas e foto`); return; }
    }
    if (foraQtdNum > 0 && foraFiles.length === 0 && (!existing || (existing.fora_lista_images || []).length < foraQtdNum)) {
      toast.error("Envie os comprovantes de Fora de Lista"); return;
    }
    if (observacoes.length > 300) { toast.error("Observações não podem ter mais de 300 caracteres"); return; }
    const devolucaoQtdNum = Number(devolucaoQtd) || 0;
    const existingDevolucaoQtd = (existing?.devolucao_comprovantes || (existing?.devolucao_comprovante ? [existing.devolucao_comprovante] : [])).length;
    if (devolucaoQtdNum > 0 && devolucaoFiles.length !== devolucaoQtdNum && existingDevolucaoQtd !== devolucaoQtdNum) {
      toast.error(`Anexe ${devolucaoQtdNum} foto(s), uma para cada devolução`); return;
    }
    const onlogQtdNum = Number(onlogQtd) || 0;
    const existingOnlogQtd = (existing?.onlog_comprovantes || (existing?.onlog_comprovante ? [existing.onlog_comprovante] : [])).length;
    if (onlogQtdNum > 0 && onlogFiles.length !== onlogQtdNum && existingOnlogQtd !== onlogQtdNum) {
      toast.error(`Anexe ${onlogQtdNum} foto(s), uma para cada remessa OnLog`); return;
    }

    const fd = new FormData();
    if (behalf) fd.append("on_behalf_driver_id", behalf);
    fd.append("entregues", modalidade === "normal" ? entregues : "0");
    fd.append("retornos", modalidade === "normal" ? retornos : "0");
    fd.append("fora_lista_qtd", String(foraQtdNum));
    fd.append("observacoes", observacoes);
    fd.append("rota_label", rotaLabel.trim());
    fd.append("regiao", regiao);
    fd.append("devolucao_qtd", String(Number(devolucaoQtd) || 0));
    fd.append("onlog_qtd", String(Number(onlogQtd) || 0));
    fd.append("remessas_ar_qtd", String(Number(remessasArQtd) || 0));
    fd.append("coletas_qtd", String(Number(coletasQtd) || 0));
    fd.append("modalidade_fechamento", modalidade);
    if (modalidade === "por_viagem") viagens.forEach((v) => {
      fd.append("viagem_rota_setor", v.rotaSetor.trim());
      fd.append("viagem_remessas_qtd", String(Number(v.remessasQtd) || 0));
      if (v.file) fd.append("viagens_comprovantes", v.file);
    });
    devolucaoFiles.forEach((f) => fd.append("devolucao_comprovantes", f));
    onlogFiles.forEach((f) => fd.append("onlog_comprovantes", f));
    if (isEdit) { fd.append("closing_id", existing.id); if (existing.updated_at) fd.append("expected_updated_at", existing.updated_at); }
    else fd.append("closing_date", closingDate);
    if (jadlog) fd.append("jadlog_print", jadlog);
    foraFiles.forEach((f) => fd.append("fora_lista_files", f));
    fd.append("abastecimento_used", fuelUsed ? "true" : "false");
    if (fuelUsed) {
      fd.append("abastecimento_valor", String(Number(String(fuelValor).replace(",", "."))));
      if (fuelData) fd.append("abastecimento_data", fuelData);
      fd.append("abastecimento_obs", fuelObs || "");
      if (fuelFile) fd.append("abastecimento_comprovante", fuelFile);
    }

    setLoading(true);
    try {
      await api.post("/closings", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(isEdit ? "Rota atualizada" : "Rota enviada para conferência!");
      navigate(behalf ? safeConferenceReturn(params.get("returnTo")) || `/admin/conferencia?driver=${behalf}&date=${closingDate}` : "/motorista", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  if (fetchError) return <div className="rounded-xl border border-red-300 bg-red-50 p-5 text-red-900"><p>Não foi possível abrir o fechamento: {fetchError}</p><button className="mt-3 underline" onClick={() => window.location.reload()}>Tentar novamente</button></div>;
  return (
    <div className="space-y-5 rounded-xl bg-[#0A0E17] p-4 text-slate-100" data-theme="driver">
      {behalf && <div className="rounded-lg bg-blue-900 p-3">Lançamento junto com {targetDriver?.nome || "o motorista…"} · responsável: {authUser?.nome}</div>}
      <button
        onClick={() => behalf ? navigate(safeConferenceReturn(params.get("returnTo")) || `/admin/conferencia?driver=${behalf}&date=${closingDate}`) : navigate(-1)}
        className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-200"
        data-testid="closing-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Voltar
      </button>

      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-400">Fechamento diário</p>
        <h1 className="mt-1 font-display text-3xl font-bold text-white">
          {isEdit ? "Editar rota" : "Nova rota"}
        </h1>
      </div>

      {/* Date picker (somente create) */}
      {!isEdit && (
        <div className="rounded-2xl border border-slate-800 bg-[#131B2B] p-4" data-tour="closing-date">
          <Label htmlFor="closing-date" className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
            <CalendarIcon className="h-4 w-4 text-blue-400" /> Data do fechamento
          </Label>
          <Input
            id="closing-date"
            type="date"
            value={closingDate}
            max={todayStr()}
            onChange={(e) => {
              const v = e.target.value;
              if (v && v <= todayStr()) setClosingDate(v);
              else toast.error("Não é possível selecionar datas futuras");
            }}
            disabled={loading}
            data-testid="closing-date-input"
            className="mt-2 h-12 bg-slate-900/60 text-base"
          />
          <p className="mt-1 text-[10px] text-slate-500">Permitido apenas hoje ou datas anteriores.</p>
        </div>
      )}

      {fetching ? (
        <p className="text-sm text-slate-500">Carregando dados…</p>
      ) : (
        <>
          {isApproved && !staffEdit && (
            <div className="flex items-start gap-3 rounded-2xl border border-green-500/30 bg-green-500/10 p-4 text-sm text-green-200" data-testid="closing-approved-banner">
              <Lock className="mt-0.5 h-4 w-4" />
              <div>
                <p className="font-semibold">Rota aprovada</p>
                <p className="opacity-90">Esta rota foi conferida pelo administrador e não pode mais ser editada.</p>
              </div>
            </div>
          )}
          {existing?.archived && <p className="rounded-xl border border-slate-600 p-3 text-sm">Fechamento consolidado no histórico, disponível somente para consulta.</p>}
          {staffEdit && isEdit && !existing?.archived && <p className="rounded-xl border border-amber-400/40 bg-amber-900/30 p-3 text-sm">Ao salvar a edição, o fechamento ficará pendente para nova conferência. O histórico guardará o responsável e as alterações.</p>}
          {(isRejected || existing?.status === "correcao") && existing?.review?.justificativa && (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200" data-testid="closing-rejected-banner">
              <p className="font-semibold">{isRejected ? "Rota rejeitada" : "Correção solicitada"}</p>
              <p className="mt-1 opacity-90">{existing.review.justificativa}</p>
              <p className="mt-1 text-xs opacity-75">Faça os ajustes e reenvie.</p>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-3 rounded-2xl border border-blue-500/30 bg-blue-500/5 p-4" data-tour="closing-type">
              <Label className="text-xs font-semibold uppercase tracking-wider text-blue-300">Tipo de fechamento</Label>
              <div className="grid grid-cols-2 gap-2">
                {[{ value: "normal", label: "Normal" }, { value: "por_viagem", label: "Por viagem" }].map((item) => (
                  <button key={item.value} type="button" disabled={lockedReadOnly} onClick={() => setModalidade(item.value)} className={`rounded-xl border px-3 py-3 text-sm font-bold uppercase tracking-wider ${modalidade === item.value ? "border-blue-500 bg-blue-500/20 text-blue-200" : "border-slate-700 bg-slate-900/40 text-slate-400"}`}>
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Rota realizada (região) - OBRIGATÓRIO */}
            <div className="space-y-2 rounded-2xl border border-blue-500/30 bg-blue-500/5 p-4" data-testid="regiao-block" data-tour="closing-region">
              <Label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-blue-300">
                <RouteIcon className="h-4 w-4" /> Lista de entrega realizada <span className="text-red-400">*</span>
              </Label>
              <p className="text-[11px] text-slate-400">Selecione a região antes de informar os números.</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => !lockedReadOnly && setRegiao("Guarulhos")}
                  disabled={lockedReadOnly}
                  data-testid="regiao-guarulhos"
                  className={`rounded-xl border px-3 py-3 text-sm font-bold uppercase tracking-wider transition ${regiao === "Guarulhos" ? "border-blue-500 bg-blue-500/20 text-blue-200" : "border-slate-700 bg-slate-900/40 text-slate-400 hover:border-slate-500"}`}
                >
                  Guarulhos
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!lockedReadOnly) {
                      setRegiao("São Paulo");
                      setOnlogQtd("");
                      setOnlogFiles([]);
                    }
                  }}
                  disabled={lockedReadOnly}
                  data-testid="regiao-sao-paulo"
                  className={`rounded-xl border px-3 py-3 text-sm font-bold uppercase tracking-wider transition ${regiao === "São Paulo" ? "border-blue-500 bg-blue-500/20 text-blue-200" : "border-slate-700 bg-slate-900/40 text-slate-400 hover:border-slate-500"}`}
                >
                  São Paulo
                </button>
              </div>
            </div>

            {modalidade === "por_viagem" && (
              <div className="space-y-3 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4" data-tour="closing-main-data">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-amber-300">Viagens realizadas</Label>
                  <p className="mt-1 text-xs text-slate-400">Adicione cada viagem separadamente.</p>
                </div>
                {viagens.map((viagem, index) => (
                  <div key={index} className="space-y-3 rounded-xl border border-slate-700 bg-slate-900/40 p-3">
                    <div className="flex items-center justify-between"><span className="text-sm font-bold text-white">Viagem {index + 1}</span>{viagens.length > 1 && !lockedReadOnly && <button type="button" onClick={() => setViagens((prev) => prev.filter((_, i) => i !== index))} className="text-red-300"><Trash2 className="h-4 w-4" /></button>}</div>
                    <Input placeholder="Rota ou setor" value={viagem.rotaSetor} disabled={lockedReadOnly} onChange={(e) => setViagens((prev) => prev.map((v, i) => i === index ? { ...v, rotaSetor: e.target.value } : v))} className="h-12 bg-slate-900/60" />
                    <Input type="number" min={1} inputMode="numeric" placeholder="Quantidade de remessas" value={viagem.remessasQtd} disabled={lockedReadOnly} onChange={(e) => setViagens((prev) => prev.map((v, i) => i === index ? { ...v, remessasQtd: e.target.value } : v))} className="h-12 bg-slate-900/60" />
                    {viagem.existing && !viagem.file && <p className="text-xs text-slate-500">Comprovante já enviado.</p>}
                    {!lockedReadOnly && <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-700 py-4 text-sm text-slate-400"><Upload className="h-4 w-4" />{viagem.file ? viagem.file.name : "Adicionar foto do comprovante"}<input type="file" accept="image/jpeg,image/png,image/jpg" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file && validImage(file)) setViagens((prev) => prev.map((v, i) => i === index ? { ...v, file } : v)); else if (file) toast.error("Use apenas JPG, JPEG ou PNG"); }} /></label>}
                  </div>
                ))}
                {!lockedReadOnly && <button type="button" onClick={() => setViagens((prev) => [...prev, { rotaSetor: "", remessasQtd: "", file: null }])} className="flex w-full items-center justify-center gap-2 rounded-xl border border-blue-500/40 py-3 text-sm font-semibold text-blue-300"><Plus className="h-4 w-4" />Adicionar outra viagem</button>}
              </div>
            )}

            {/* Rota label */}
            {modalidade === "normal" && <div className="space-y-4" data-tour="closing-main-data">
            <div className="space-y-1.5">
              <Label htmlFor="rota-label" className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                <RouteIcon className="h-4 w-4 text-blue-400" /> Identificação da rota <span className="text-red-400">*</span>
              </Label>
              <Input
                id="rota-label"
                data-testid="closing-rota-label-input"
                placeholder="Ex: ZL-04 manhã, Centro tarde…"
                value={rotaLabel}
                onChange={(e) => setRotaLabel(e.target.value)}
                disabled={lockedReadOnly}
                className="h-12 bg-slate-900/60"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <NumField id="entregues" label="Entregues" value={entregues} onChange={setEntregues} disabled={lockedReadOnly} testId="closing-entregues-input" />
              <NumField id="retornos" label="Retornos" value={retornos} onChange={setRetornos} disabled={lockedReadOnly} testId="closing-retornos-input" />
            </div>

            <div className="grid grid-cols-2 gap-3 rounded-2xl border border-slate-800 bg-[#131B2B] p-4">
              <Calc label="Total" value={total} />
              <Calc label="Sucesso" value={`${sucesso}%`} accent="text-blue-400" />
            </div>

            {/* Jadlog upload */}
            <div className="space-y-2" data-tour="closing-proof">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                Print da Jadlog {!isEdit && <span className="text-red-400">*</span>}
              </Label>
              {jadlog ? (
                <div className="flex items-center justify-between rounded-2xl border border-blue-500/40 bg-blue-500/10 p-3 text-sm text-blue-200">
                  <span className="truncate">{jadlog.name}</span>
                  <button type="button" onClick={() => setJadlog(null)} className="text-red-300" aria-label="Remover">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : existing?.jadlog_image ? (
                <p className="rounded-2xl border border-slate-800 bg-slate-800/40 p-3 text-xs text-slate-400">
                  Print já enviado · selecione outro arquivo para substituir
                </p>
              ) : null}
              {!lockedReadOnly && (
                <button
                  type="button"
                  onClick={() => jadlogRef.current?.click()}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-700 bg-slate-900/40 py-6 text-sm font-medium text-slate-400 hover:border-blue-500 hover:text-blue-300"
                  data-testid="closing-jadlog-upload"
                >
                  <Upload className="h-4 w-4" /> {jadlog ? "Trocar print" : (existing?.jadlog_image ? "Substituir print" : "Selecionar imagem")}
                </button>
              )}
              <input ref={jadlogRef} type="file" accept="image/jpeg,image/png,image/jpg" className="hidden" onChange={onPickJadlog} />
            </div>
            </div>}

            {/* Fora de Lista e demais informações opcionais */}
            <div data-tour="closing-extras" className="space-y-4">
            <div className="space-y-2 rounded-2xl border border-slate-800 bg-[#131B2B] p-4">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Fora de lista — quantidade</Label>
              <Input
                type="number" min={0} inputMode="numeric"
                value={foraQtd} onChange={(e) => setForaQtd(e.target.value)}
                disabled={lockedReadOnly}
                data-testid="closing-fora-qtd-input"
                className="h-12 bg-slate-900/60"
              />
              {foraQtdNum > 0 && (
                <div className="mt-2 space-y-2">
                  <p className="text-xs text-amber-300">
                    Anexe {foraQtdNum} comprovante{foraQtdNum > 1 ? "s" : ""}
                    {existing && existing.fora_lista_images?.length > 0 && (
                      <span className="text-slate-500"> · {existing.fora_lista_images.length} já enviadas</span>
                    )}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {foraFiles.map((f, idx) => (
                      <div key={idx} className="relative overflow-hidden rounded-xl border border-slate-700">
                        <img src={URL.createObjectURL(f)} alt="" className="h-24 w-full object-cover" />
                        <button type="button" onClick={() => removeFora(idx)} className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white" aria-label="Remover">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {!lockedReadOnly && (
                    <button
                      type="button"
                      onClick={() => foraRef.current?.click()}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-700 bg-slate-900/40 py-4 text-sm font-medium text-slate-400 hover:border-blue-500 hover:text-blue-300"
                      data-testid="closing-fora-upload"
                    >
                      <ImagePlus className="h-4 w-4" /> Adicionar comprovante
                    </button>
                  )}
                  <input ref={foraRef} type="file" multiple accept="image/jpeg,image/png,image/jpg" className="hidden" onChange={onPickFora} />
                </div>
              )}
            </div>

            <div className="space-y-3 rounded-2xl border border-slate-800 bg-[#131B2B] p-4">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Lista de devolução (opcional)</Label>
              <Input type="number" min={0} inputMode="numeric" value={devolucaoQtd} onChange={(e) => setDevolucaoQtd(e.target.value)} disabled={lockedReadOnly} placeholder="Quantidade" data-testid="devolucao-qtd-input" className="h-12 bg-slate-900/60" />
              {(Number(devolucaoQtd) || 0) > 0 && !lockedReadOnly && (
                <>
                  <button type="button" onClick={() => devolucaoRef.current?.click()} className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-700 py-4 text-sm text-slate-400">
                    <ImagePlus className="h-4 w-4" /> {devolucaoFiles.length > 0 ? `${devolucaoFiles.length} de ${Number(devolucaoQtd)} fotos selecionadas` : `Adicionar ${Number(devolucaoQtd)} foto(s)`}
                  </button>
                  <p className="text-xs text-slate-500">É obrigatória uma foto para cada devolução informada.</p>
                  <input ref={devolucaoRef} type="file" multiple accept="image/jpeg,image/png,image/jpg" className="hidden" onChange={onPickDevolucao} />
                </>
              )}
            </div>

            {regiao === "Guarulhos" && (
              <div className="space-y-3 rounded-2xl border border-slate-800 bg-[#131B2B] p-4">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Remessas OnLog (opcional)</Label>
                <Input type="number" min={0} inputMode="numeric" value={onlogQtd} onChange={(e) => setOnlogQtd(e.target.value)} disabled={lockedReadOnly} placeholder="Quantidade" data-testid="onlog-qtd-input" className="h-12 bg-slate-900/60" />
                {(Number(onlogQtd) || 0) > 0 && !lockedReadOnly && (
                  <>
                    <button type="button" onClick={() => onlogRef.current?.click()} className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-700 py-4 text-sm text-slate-400">
                      <Upload className="h-4 w-4" /> {onlogFiles.length > 0 ? `${onlogFiles.length} de ${Number(onlogQtd)} fotos selecionadas` : `Adicionar ${Number(onlogQtd)} foto(s)`}
                    </button>
                    <p className="text-xs text-slate-500">É obrigatória uma foto para cada remessa OnLog informada.</p>
                    <input ref={onlogRef} type="file" multiple accept="image/jpeg,image/png,image/jpg" className="hidden" onChange={onPickOnlog} />
                  </>
                )}
              </div>
            )}

            {user?.faz_rota_ar && (
              <div className="space-y-2 rounded-2xl border border-slate-800 bg-[#131B2B] p-4">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Remessas AR realizadas (opcional)</Label>
                <Input type="number" min={0} inputMode="numeric" value={remessasArQtd} onChange={(e) => setRemessasArQtd(e.target.value)} disabled={lockedReadOnly} data-testid="remessas-ar-qtd-input" className="h-12 bg-slate-900/60" />
              </div>
            )}

            <div className="space-y-2 rounded-2xl border border-slate-800 bg-[#131B2B] p-4">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Coletas realizadas (opcional)</Label>
              <Input
                type="number"
                min={0}
                inputMode="numeric"
                value={coletasQtd}
                onChange={(e) => setColetasQtd(e.target.value)}
                disabled={lockedReadOnly}
                placeholder="Quantidade de coletas"
                data-testid="coletas-qtd-input"
                className="h-12 bg-slate-900/60"
              />
            </div>
            </div>

            {/* Abastecimento */}
            <div className="space-y-3 rounded-2xl border border-slate-800 bg-[#131B2B] p-4" data-tour="closing-fuel">
              <div className="flex items-center gap-2">
                <Fuel className="h-4 w-4 text-blue-400" />
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Abastecimento</Label>
              </div>
              <p className="text-xs text-slate-500">Utilizou o cartão de abastecimento da D1 hoje?</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => !lockedReadOnly && setFuelUsed(true)}
                  disabled={lockedReadOnly}
                  data-testid="fuel-yes"
                  className={`rounded-xl border px-3 py-3 text-sm font-bold uppercase tracking-wider transition ${fuelUsed ? "border-blue-500 bg-blue-500/15 text-blue-300" : "border-slate-700 bg-slate-900/40 text-slate-400 hover:border-slate-500"}`}
                >
                  Sim
                </button>
                <button
                  type="button"
                  onClick={() => !lockedReadOnly && setFuelUsed(false)}
                  disabled={lockedReadOnly}
                  data-testid="fuel-no"
                  className={`rounded-xl border px-3 py-3 text-sm font-bold uppercase tracking-wider transition ${!fuelUsed ? "border-slate-500 bg-slate-700/40 text-slate-200" : "border-slate-700 bg-slate-900/40 text-slate-400 hover:border-slate-500"}`}
                >
                  Não
                </button>
              </div>

              {fuelUsed && (
                <div className="space-y-3 border-t border-slate-800 pt-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Valor (R$)</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="0,00"
                      value={fuelValor}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^\d,\.]/g, "");
                        setFuelValor(v);
                      }}
                      disabled={lockedReadOnly}
                      data-testid="fuel-valor-input"
                      className="h-12 bg-slate-900/60 font-mono text-base"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Data do abastecimento</Label>
                    <Input
                      type="date"
                      value={fuelData || closingDate}
                      max={closingDate || todayStr()}
                      onChange={(e) => setFuelData(e.target.value)}
                      disabled={lockedReadOnly}
                      data-testid="fuel-data-input"
                      className="h-12 bg-slate-900/60"
                    />
                    <p className="text-[10px] text-slate-500">Permitido apenas a data do fechamento ou anteriores</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      Comprovante (JPG, PNG ou PDF) <span className="text-red-400">*</span>
                    </Label>
                    {fuelFile ? (
                      <div className="flex items-center justify-between rounded-xl border border-blue-500/40 bg-blue-500/10 p-3 text-sm text-blue-200">
                        <span className="inline-flex items-center gap-2 truncate">
                          {fuelFile.type === "application/pdf" ? <FileText className="h-4 w-4" /> : <ImagePlus className="h-4 w-4" />}
                          {fuelFile.name}
                        </span>
                        <button type="button" onClick={() => setFuelFile(null)} className="text-red-300" aria-label="Remover">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : existing?.abastecimento_comprovante ? (
                      <p className="rounded-xl border border-slate-800 bg-slate-800/40 p-3 text-xs text-slate-400">
                        Comprovante já enviado · selecione outro para substituir
                      </p>
                    ) : null}
                    {!lockedReadOnly && (
                      <button
                        type="button"
                        onClick={() => fuelRef.current?.click()}
                        className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-700 bg-slate-900/40 py-4 text-sm font-medium text-slate-400 hover:border-blue-500 hover:text-blue-300"
                        data-testid="fuel-upload-btn"
                      >
                        <Upload className="h-4 w-4" /> {fuelFile ? "Trocar arquivo" : "Selecionar comprovante"}
                      </button>
                    )}
                    <input
                      ref={fuelRef}
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (!validFuelComp(f)) { toast.error("Use JPG, PNG ou PDF"); return; }
                        setFuelFile(f);
                      }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Observações (opcional)</Label>
                    <Textarea
                      value={fuelObs}
                      onChange={(e) => setFuelObs(e.target.value.slice(0, 300))}
                      rows={2}
                      maxLength={300}
                      disabled={lockedReadOnly}
                      data-testid="fuel-obs-input"
                      placeholder="Ex: Posto Shell - Av. Paulista"
                      className="bg-slate-900/60"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Observações */}
            <div className="space-y-2">
              <Label htmlFor="obs" className="text-xs font-semibold uppercase tracking-wider text-slate-400">Observações (opcional)</Label>
              <Textarea
                id="obs" data-testid="closing-obs-input"
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value.slice(0, 300))}
                maxLength={300} rows={3}
                disabled={lockedReadOnly}
                className="bg-slate-900/60"
                placeholder="Ex: trânsito intenso, cliente ausente, etc."
              />
              <p className="text-[10px] text-slate-500">{observacoes.length}/300</p>
            </div>

            {!lockedReadOnly && (
              <Button
                type="submit"
                disabled={loading}
                data-testid="closing-submit-button"
                className="glow-primary h-14 w-full rounded-xl bg-blue-600 text-base font-bold uppercase tracking-wider hover:bg-blue-500"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                  <span className="inline-flex items-center gap-2">
                    <Send className="h-5 w-5" /> {isEdit ? "Atualizar rota" : "Enviar rota"}
                  </span>
                )}
              </Button>
            )}
          </form>
        </>
      )}
    </div>
  );
}

function NumField({ id, label, value, onChange, disabled, testId }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</Label>
      <Input id={id} type="number" min={0} inputMode="numeric"
        value={value} onChange={(e) => onChange(e.target.value)}
        disabled={disabled} data-testid={testId}
        className="h-14 bg-slate-900/60 text-center font-display text-2xl font-bold"
      />
    </div>
  );
}
function Calc({ label, value, accent = "text-white" }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-display text-2xl font-bold ${accent}`}>{value}</p>
    </div>
  );
}
